import { PublicControllerApi, AdminControllerApi, UserControllerApi } from './generated/cortex-api';
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import axios, { AxiosRequestConfig, AxiosError } from 'axios'

interface RetryConfig extends AxiosRequestConfig {
    retried: boolean;
}

export const withCredentialsRetries: RetryConfig = {
    retried: false,
    withCredentials: true
};

export class Environment {

    private static instance: Environment;
    private constructor(gatewayURL: string, launchpadBasePath: string, configuration: [string, string, string][], authorizeUrl: string, clientId: string, projectName: string, plausibleSite: string) {
        this._launchpadPublicControllerApi = new PublicControllerApi(undefined, launchpadBasePath, axios)
        this._launchpadUserControllerApi = new UserControllerApi(undefined, launchpadBasePath, axios)
        this._launchpadAdminControllerApi = new AdminControllerApi(undefined, launchpadBasePath, axios)
        this._configuration = configuration
        this._projectName = projectName
        this._launchpadBasePath = launchpadBasePath;
        this._gatewayURL = gatewayURL
        this._clientId = clientId
        this._plausibleSite = plausibleSite

        const callback = window.location.origin + "/oauthcallback";
        this._refreshToken = window.location.origin + "/refreshtoken";

        this._loginUrl =
          authorizeUrl +
          "?response_type=code" +
          "&client_id=" + encodeURIComponent(clientId) +
          "&redirect_uri=" + encodeURIComponent(callback);

        axios.defaults.headers.common['Accept'] = 'application/json, text/plain'; // remove the default */* otherwise will get redirect to 302 (login-page) instead of 401

        axios.interceptors.response.use(
            response => response,
            error => {
              const originalRequest = error.config;
        

              if (error?.response?.status === 401) {
                if (!originalRequest || originalRequest._retried) {
                    this.login();
                    return;
                }
          
                originalRequest._retried = true;

                return this.refreshToken().then(() => {
                    return axios.request(originalRequest);
                  })
                  .catch(() => {
                    this.login();
                  });
              }
          
              return Promise.reject(error);
            }
          );
    }

    private _launchpadPublicControllerApi: PublicControllerApi
    private _launchpadAdminControllerApi: AdminControllerApi
    private _launchpadUserControllerApi: UserControllerApi
    private _configuration: [string, string, string][]
    private _loginUrl: string
    private _projectName: string;
    private _launchpadBasePath: string;
    private _gatewayURL: string;
    private _clientId: string;
    private _refreshToken: string;
    private _plausibleSite: string;


    public get launchpadPublicControllerApi() {
        return this._launchpadPublicControllerApi;
    }

    public get launchpadAdminControllerApi() {
        return this._launchpadAdminControllerApi;
    }

    public get launchpadUserControllerApi() {
        return this._launchpadUserControllerApi;
    }

    public get configuration() {
        return this._configuration;
    }

    public get loginUrl() {
        return this._loginUrl;
    }

    public get projectName() {
        return this._projectName;
    }

    public get launchpadBasePath() {
        return this._launchpadBasePath;
    }

    public get gatewauUrl() {
        return this._gatewayURL;
    }

    public get plausibleSite() {
        return this._plausibleSite;
    }

    private initSentry = (sentryDNS: string, sentryEnvironment: string, version: string) => {
        Sentry.init({
            dsn: sentryDNS,
            environment: sentryEnvironment,
            release: version,
            integrations: [new BrowserTracing()],
            tracesSampleRate: 1.0,
        });
    }

    public static async getInstance(): Promise<Environment> {
        if (!Environment.instance) {
            return axios.get("/config")
                .then((result) => {
                    Environment.instance = new Environment(result.data.LAUNCHPAD_API_GATEWAY,
                        result.data.LAUNCHPAD_API_GATEWAY + "/cortex",
                        JSON.parse(result.data.CONFIGURATION),
                        Boolean(result.data.AUTHORIZE_URL) ? result.data.AUTHORIZE_URL : result.data.LAUNCHPAD_API_GATEWAY + '/oauth2/authorize',
                        result.data.CLIENT_ID,
                        result.data.LAUNCHPAD_PROJECT_NAME,
                        result.data.PLAUSIBLE_SITE)

                    if (result.data.SENTRY_DSN && result.data.SENTRY_ENVIRONMENT && process.env.VERSION) {
                        Environment.instance.initSentry(result.data.SENTRY_DSN, result.data.SENTRY_ENVIRONMENT, process.env.VERSION)
                    }

                    return Environment.instance;
                })
        }

        return Environment.instance;
    }

    public async logout() {
        // 1) Clear gateway session cookie (JSESSIONID) on the gateway origin
        await axios.post(`${this.gatewauUrl}/api/clearsession`, null, { withCredentials: true });
      
        // 2) Clear app cookies + get final logout redirect URL
        const { data } = await axios.post("/logoff", null, { withCredentials: true });
      
        // 3) Navigate (force a real navigation even if same URL)
        const url = data.logoutUrl.includes("?")
          ? `${data.logoutUrl}&_=${Date.now()}`
          : `${data.logoutUrl}?_=${Date.now()}`;
      
        window.location.replace(url);
      }


    public async refreshToken(): Promise<void> {
        await axios.get(this._refreshToken, { withCredentials: true });
    }

    public login() {
        const state = encodeURIComponent(
            JSON.stringify({
                clientId: this._clientId,
                returnTo: window.location.href
            })
        );

        window.location.href = this.loginUrl + `&state=${state}`;
    }
}